import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { WalletBtns, WalletContainer, WalletHeader } from "../walletStyles";
import { generateAccountDetails } from "../../../screens/dashboard/DashboardUtils";
import { savePaymentDetails } from "../../../redux/WalletSlice";

const DepositAccountDetails = ({
	onCloseModal,
	onReturn,
	paymentChannel,
	amountToDeposit,
	verifyPaymentLoading,
	onVerify,
}) => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const { paymentAccount } = useSelector((state) => state.wallet);
	const [loading, setLoading] = useState(false);

	// FUNCTION TO FETCH ACCOUNT DETAILS
	const fetchAccountDetails = async () => {
		setLoading(true);
		try {
			const response = await generateAccountDetails();

			dispatch(savePaymentDetails(response));
		} catch (error) {
			if (error === "This session has expired. Please Login again") {
				navigate("/login");
			} else {
				toast.error(error);
			}
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		if (paymentAccount) {
			return;
		} else {
			fetchAccountDetails();
		}
	}, []);

	return (
		<WalletContainer>
			<>
				<WalletHeader
					headerText='Transfer'
					onCloseModal={onCloseModal}
					context='Kindly transfer into the account below then, click on the completed button to verify transaction. A mail will be sent on confirmation.'
				/>
				<Box
					display='flex'
					justifyContent={loading ? "center" : "start"}
					marginTop={3}
					marginBottom={loading ? 4 : 2}>
					{loading ? (
						<CircularProgress color='primary' />
					) : paymentAccount ? (
						<>
							{paymentChannel === "paga-bank-transfer" && (
								<AccountDetails
									amountToDeposit={amountToDeposit}
									accountNumber={paymentAccount?.paga?.accountNumber || "N/A"}
									accountName={`Fortcore Limited - ${paymentAccount?.paga?.accountName}` || "N/A"}
									bankName='Paga'
								/>
							)}
							{paymentChannel === "providus-bank-transfer" && (
								<AccountDetails
									amountToDeposit={amountToDeposit}
									accountNumber={paymentAccount?.providus?.account_number || "N/A"}
									accountName={paymentAccount?.providus?.account_name || "N/A"}
									bankName='Providus'
								/>
							)}
						</>
					) : (
						<AccountError onRetry={fetchAccountDetails} />
					)}
				</Box>
				{paymentAccount && (
					<WalletBtns
						btnText='completed'
						whiteBtnClick={onReturn}
						isLoading={verifyPaymentLoading}
						greenBtnClick={onVerify}
					/>
				)}
			</>
		</WalletContainer>
	);
};

export default DepositAccountDetails;

DepositAccountDetails.propTypes = {
	onCloseModal: PropTypes.func,
	onReturn: PropTypes.func,
	amountToDeposit: PropTypes.string,
	paymentChannel: PropTypes.string,
	verifyPaymentLoading: PropTypes.bool,
	onVerify: PropTypes.func,
};

const AccountDetails = ({ amountToDeposit, accountName, accountNumber, bankName }) => {
	return (
		<Box display='flex' flexDirection='column' fontSize='13px' gap={2}>
			<Box display='flex' gap={3} alignItems='center'>
				<Typography sx={{ color: "#717E83", width: "130px" }}>Amount (NGN):</Typography>
				<Typography sx={{ color: "#717E83" }}>{amountToDeposit}</Typography>
			</Box>
			<Box display='flex' gap={3} alignItems='center'>
				<Typography sx={{ color: "#717E83", width: "130px" }}>Bank Name:</Typography>
				<Typography sx={{ color: "#717E83" }}>{bankName}</Typography>
			</Box>
			<Box display='flex' gap={3} alignItems='center'>
				<Typography sx={{ color: "#717E83", width: "130px" }}>Account Number:</Typography>
				<Typography sx={{ color: "#717E83" }}>{accountNumber}</Typography>
			</Box>
			<Box display='flex' gap={3} alignItems='start'>
				<Typography sx={{ color: "#717E83", width: "130px" }}>Account Name:</Typography>
				<Typography sx={{ color: "#717E83" }}>{accountName}</Typography>
			</Box>
		</Box>
	);
};

const AccountError = ({ onRetry }) => (
	<Box display='flex' flexDirection='column' justifyContent='center' gap={2}>
		<Typography sx={{ color: "red" }}>
			Failed to generate account information. Click the button below to retry.
		</Typography>
		<Button variant='contained' color='primary' onClick={onRetry}>
			retry
		</Button>
	</Box>
);

AccountDetails.propTypes = {
	accountName: PropTypes.string,
	accountNumber: PropTypes.string,
	amountToDeposit: PropTypes.string,
	bankName: PropTypes.string,
};

AccountError.propTypes = {
	onRetry: PropTypes.func,
};
